import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import {
  useCompanySettings,
  useUpdateCompanySettings,
} from "@/hooks/useCompanySettings";
import { CompanySettingsUpdate } from "@/types/companySettings";

const timePattern = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;

const validateTimeFormat = (value: string) => {
  if (!value) return "時間不能為空";
  
  // Check basic format (HH:MM)
  if (!/^\d{2}:\d{2}$/.test(value)) {
    return "時間格式必須為 HH:MM (例如: 08:30)";
  }
  
  const [hours, minutes] = value.split(':').map(Number);
  
  // Validate hours (00-23)
  if (hours < 0 || hours > 23) {
    return "小時必須在 00-23 之間";
  }
  
  // Validate minutes (00-59)
  if (minutes < 0 || minutes > 59) {
    return "分鐘必須在 00-59 之間";
  }
  
  return true;
};

const formSchema = z.object({
  workingHoursStart: z
    .string()
    .refine((val) => validateTimeFormat(val) === true, {
      message: "請輸入有效的時間格式 (HH:MM)",
    }),
  workingHoursEnd: z
    .string()
    .refine((val) => validateTimeFormat(val) === true, {
      message: "請輸入有效的時間格式 (HH:MM)",
    }),
  lunchStart: z
    .string()
    .refine((val) => validateTimeFormat(val) === true, {
      message: "請輸入有效的時間格式 (HH:MM)",
    }),
  lunchEnd: z
    .string()
    .refine((val) => validateTimeFormat(val) === true, {
      message: "請輸入有效的時間格式 (HH:MM)",
    }),
  workingDays: z.array(z.string()).min(1, "請至少選擇一個工作日"),
  timezone: z.string().min(1, "請選擇時區"),
  overtimeAllowed: z.boolean(),
  maxOvertimeHours: z.number().min(0).max(24),
  defaultSchedulingMode: z.enum([
    "forward",
    "dueDatePriority",
    "priorityFirst",
    "hybrid",
  ] as const),
});

const weekDays = [
  { value: "1", label: "週一" },
  { value: "2", label: "週二" },
  { value: "3", label: "週三" },
  { value: "4", label: "週四" },
  { value: "5", label: "週五" },
  { value: "6", label: "週六" },
  { value: "7", label: "週日" },
];

const schedulingModes = [
  { value: "forward", label: "儘早開始 (Forward)" },
  { value: "dueDatePriority", label: "交期優先 (Due Date Priority)" },
  { value: "priorityFirst", label: "優先級優先 (Priority First)" },
  { value: "hybrid", label: "混合模式 (Hybrid)" },
];

// Helper function to format time input
const formatTimeInput = (value: string): string => {
  // Remove non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 4 digits
  const limitedDigits = digits.slice(0, 4);
  
  // Add colon after 2 digits
  if (limitedDigits.length >= 3) {
    return `${limitedDigits.slice(0, 2)}:${limitedDigits.slice(2)}`;
  } else if (limitedDigits.length >= 1) {
    return limitedDigits;
  }
  
  return '';
};

// Helper function to handle time input change
const handleTimeInputChange = (
  value: string,
  onChange: (value: string) => void
) => {
  const formatted = formatTimeInput(value);
  onChange(formatted);
};

export function CompanySettings() {
  const { toast } = useToast();
  const { data: settings, isLoading, error } = useCompanySettings();
  const updateSettings = useUpdateCompanySettings();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workingHoursStart: "08:30",
      workingHoursEnd: "17:30",
      lunchStart: "12:00",
      lunchEnd: "13:30",
      workingDays: ["1", "2", "3", "4", "5"],
      timezone: "Asia/Taipei",
      overtimeAllowed: false,
      maxOvertimeHours: 2,
      defaultSchedulingMode: "forward",
    },
  });

  useEffect(() => {
    if (settings) {
      // Convert snake_case scheduling mode from backend to camelCase for frontend
      const backendToFrontendModeMap: Record<string, string> = {
        'forward': 'forward',
        'due_date_priority': 'dueDatePriority',
        'priority_first': 'priorityFirst',
        'hybrid': 'hybrid'
      };
      
      const workingDaysArray = settings.workingDays.split(",");
      form.reset({
        workingHoursStart: settings.workingHoursStart,
        workingHoursEnd: settings.workingHoursEnd,
        lunchStart: settings.lunchStart,
        lunchEnd: settings.lunchEnd,
        workingDays: workingDaysArray,
        timezone: settings.timezone,
        overtimeAllowed: settings.overtimeAllowed,
        maxOvertimeHours: settings.maxOvertimeHours,
        defaultSchedulingMode: (backendToFrontendModeMap[settings.defaultSchedulingMode] || settings.defaultSchedulingMode) as "forward" | "dueDatePriority" | "priorityFirst" | "hybrid",
      });
    }
  }, [settings, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      // Convert camelCase scheduling mode to snake_case for backend
      const schedulingModeMap: Record<string, string> = {
        'forward': 'forward',
        'dueDatePriority': 'due_date_priority',
        'priorityFirst': 'priority_first',
        'hybrid': 'hybrid'
      };
      
      const updateData: CompanySettingsUpdate = {
        ...values,
        workingDays: values.workingDays.join(","),
        defaultSchedulingMode: schedulingModeMap[values.defaultSchedulingMode] as any,
      };

      await updateSettings.mutateAsync(updateData);

      toast({
        title: "設定已更新",
        description: "公司設定已成功更新",
      });
    } catch (error) {
      toast({
        title: "更新失敗",
        description: "無法更新公司設定，請稍後再試",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500">載入設定時發生錯誤</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>工作時間設定</CardTitle>
            <CardDescription>設定公司的工作時間和休息時間</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="workingHoursStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>上班時間</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="08:30" 
                        maxLength={5}
                        value={field.value}
                        onChange={(e) => handleTimeInputChange(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="workingHoursEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>下班時間</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="17:30" 
                        maxLength={5}
                        value={field.value}
                        onChange={(e) => handleTimeInputChange(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="lunchStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>午休開始時間</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="12:00" 
                        maxLength={5}
                        value={field.value}
                        onChange={(e) => handleTimeInputChange(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lunchEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>午休結束時間</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="13:30" 
                        maxLength={5}
                        value={field.value}
                        onChange={(e) => handleTimeInputChange(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="workingDays"
              render={() => (
                <FormItem>
                  <FormLabel>工作日</FormLabel>
                  <div className="flex flex-wrap gap-4">
                    {weekDays.map((day) => (
                      <FormField
                        key={day.value}
                        control={form.control}
                        name="workingDays"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={day.value}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(day.value)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([
                                          ...field.value,
                                          day.value,
                                        ])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== day.value
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {day.label}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>時區</FormLabel>
                  <FormControl>
                    <Input
                      value="Asia/Taipei (台北時間 UTC+8)"
                      disabled
                      className="bg-muted cursor-not-allowed"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>排程設定</CardTitle>
            <CardDescription>設定排程行為和加班規則</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="overtimeAllowed"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">允許加班</FormLabel>
                    <FormDescription>
                      是否允許排程超出正常工作時間
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="maxOvertimeHours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>每日最大加班時數</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormDescription>每天允許的最大加班時數</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="defaultSchedulingMode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>預設排程模式</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="選擇排程模式" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {schedulingModes.map((mode) => (
                        <SelectItem key={mode.value} value={mode.value}>
                          {mode.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>新建排程時的預設模式</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={updateSettings.isPending}>
            {updateSettings.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            儲存設定
          </Button>
        </div>
      </form>
    </Form>
  );
}
